import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Row } from "@/components";
import { FormTextInput } from "@/components/form/FormTextInput";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useSendEmail } from "@/hooks/mutations/useSendEmail";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";

const sendEmailSchema = z.object({
  subject: z.string().trim().min(1, "Required"),
  body: z.string().trim().min(1, "Required"),
});

export const SendEmailDialog = ({
  candidacyId,
  onOpenChange,
  open,
  screenName,
  talentId,
  to,
}: {
  candidacyId?: string;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  screenName: MIXPANEL_SCREENS;
  talentId: number;
  to: string;
}) => {
  const { mutate: sendEmail, isPending } = useSendEmail();
  const successToast = useSuccessToast();

  const form = useForm<z.infer<typeof sendEmailSchema>>({
    resolver: zodResolver(sendEmailSchema),
    defaultValues: { subject: "", body: "" },
  });

  const handleSubmit = (data: z.infer<typeof sendEmailSchema>) => {
    mixpanelUtils.trackFormSubmission("Send Email", screenName);
    sendEmail(
      {
        sendEmailRequest: {
          subject: data.subject,
          body: data.body,
          candidacyId,
          talentId,
        },
      },
      {
        onSuccess: () => {
          successToast({ description: "Email sent." });
          form.reset();
          onOpenChange(false);
        },
      },
    );
  };

  return (
    <Dialog
      onOpenChange={(nextOpen) => {
        if (!nextOpen) form.reset();
        onOpenChange(nextOpen);
      }}
      open={open}
    >
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-700">
            Send Email
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            className="flex flex-col gap-4"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <FormItem className="text-slate-700">
              <FormLabel>To</FormLabel>
              <FormControl>
                <Input disabled value={to} />
              </FormControl>
            </FormItem>
            <FormTextInput
              fieldName="subject"
              form={form}
              label="Subject"
              placeholder="Subject"
            />
            <FormField
              control={form.control}
              name="body"
              render={({ field }) => (
                <FormItem className="text-slate-700">
                  <FormLabel>Body</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Body" rows={10} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end">
              <Button
                className="rounded-full"
                disabled={isPending}
                type="submit"
              >
                {isPending ? "Sending..." : "Send"}
              </Button>
            </Row>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
